<template>
  <layout-basic>
    <page-hero
      title="Leadership Plus"
      lead="IMPROVE gives you the platform and core learning. Leadership Plus is an optional extra that gives you deeper management and leadership development when you're ready."
    >
      <p class="hero-sub">Produced to ILM standards. Develop better managers and build a stronger business.</p>
    </page-hero>

    <section class="demo-video-section">
      <v-container style="max-width: 960px">
        <demo-video video-key="leadership" />
      </v-container>
    </section>

    <section class="programmes-section">
      <v-container>
        <h2 class="font-sora programmes__title">Three programmes, 50+ modules</h2>
        <div class="programmes">
          <article v-for="item in programmes" :key="item.title" class="programme-card">
            <h3 class="font-sora">{{ item.title }}</h3>
            <p>{{ item.body }}</p>
            <ul class="programme-modules">
              <li v-for="m in item.modules" :key="m">{{ m }}</li>
            </ul>
          </article>
        </div>
      </v-container>
    </section>

    <!-- Pricing exactly as the September 2026 brochure (page 9): an annual fee
         on a three year agreement, by user band. Leadership Plus is bought by
         enquiry, not through the online checkout, hence the contact CTA. -->
    <section class="lp-pricing-section">
      <v-container>
        <h2 class="font-sora programmes__title">Leadership Plus pricing</h2>
        <p class="lp-pricing__lead">Annual fee on a three year agreement, for every manager in your business.</p>
        <div class="lp-pricing">
          <div v-for="tier in pricing" :key="tier.users" class="lp-pricing__tier">
            <span class="lp-pricing__users">{{ tier.users }}</span>
            <span class="lp-pricing__fee">{{ tier.fee }}</span>
            <span class="lp-pricing__per">per year</span>
          </div>
        </div>
        <p class="lp-pricing__note">All prices exclusive of VAT. Leadership Plus is an optional bolt-on to any IMPROVE package.</p>
        <div class="lp-pricing__cta">
          <router-link to="/contact" class="lp-pricing__btn">Enquire to enhance your package</router-link>
        </div>
      </v-container>
    </section>

    <point-grid
      title="Optional ILM Accreditation"
      :points="points"
      :columns="2"
      tint
    />

    <v-container>
      <quote-block
        quote="Great businesses are built by great managers."
        attribution="Steve Walker, Founder"
      />
    </v-container>

    <standard-cta
      title="Build stronger leaders."
      lead="Watch the 5-minute demo or experience IMPROVE and explore Leadership Plus for your managers."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import QuoteBlock from '@/components/marketing/QuoteBlock.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import DemoVideo from '@/components/DemoVideo.vue';
import { marketingPageMeta } from '@/utils/marketingPage';

export default {
  name: 'LeadershipPlus',
  components: {
    LayoutBasic,
    PageHero,
    PointGrid,
    QuoteBlock,
    StandardCta,
    DemoVideo,
  },
  metaInfo() {
    return marketingPageMeta({
      title: 'Leadership Plus | Develop Better Managers',
      description:
        'Leadership Plus: 50+ management and leadership modules for aspiring, first line and mid to senior level managers, produced to ILM standards. An optional bolt-on to IMPROVE from £1,995 a year.',
      path: '/leadership-plus',
    });
  },
  data() {
    return {
      // Module titles transcribed from the September 2026 brochure, page 9.
      programmes: [
        {
          title: 'Aspiring Manager',
          body: 'Help high-potential employees build the foundations of people leadership before they step into the role.',
          modules: [
            'Effective Communication',
            'Effective Leadership',
            'Handling Difficult Conversations',
            'Leading Essentials',
            'Managing Equality and Diversity',
            'Managing Workplace Projects',
            'Managing Yourself',
            'Planning and Allocating Work',
            'Planning Change in the Workplace',
            'Satisfying Customer Requirements',
            'Setting Team Objectives',
            'Solving Problems & Making Decisions',
            'Training & Coaching Essentials',
            'Understanding How to Lead',
            'Effective Meetings',
            'Understanding Performance Management',
            'Working Within Organisational and Legal Guidelines',
          ],
        },
        {
          title: 'First Line Manager',
          body: 'Practical skills for managers leading teams day to day: communication, performance, coaching and confidence.',
          modules: [
            'Building a Positive Team Culture',
            'Conflict Resolution Strategies',
            'Creative Problem Solving',
            'Decision Making',
            'Defining Leadership',
            'Effective Delegation',
            'Forming High-Performing Teams',
            'Implementing Change Effectively',
            'Implementing Solutions',
            'Key Attributes of Effective Leaders',
            'Leadership Myths & Realities',
            'Leadership Styles',
            'Leading with Purpose',
            'Mastering Time Management',
            'Maximising Productivity',
            'Navigating Change',
            'Sustaining Change',
            'Team Dynamics & Diversity',
            'Understanding Conflict',
          ],
        },
        {
          title: 'Mid to Senior Level Manager',
          body: 'Develop experienced leaders who shape culture, strategy and stronger businesses.',
          modules: [
            'Exploring Strategic Leadership',
            'Developing Strategic Management and Leadership Skills',
            'Managing Innovation in Organisations',
            'Leading Change in Organisations',
            'Creating High Performance Teams',
            'Building Agile & Resilient Teams',
            'Introduction to Strategic Planning',
            'Managing Remote Teams',
            'Managing Resources',
            'Effective Resource Allocation',
            'Financial Awareness and Decision Making',
            'Creating a Coaching Culture',
            'Strategic Decision-Making Processes',
            'Innovative Problem-Solving',
            'Reflective Practice for Professional Development',
            'Self-Awareness & Personal Development Planning',
          ],
        },
      ],
      // Brochure page 9: annual fee, three year agreement.
      pricing: [
        { users: '1 to 50 users', fee: '£1,995' },
        { users: '51 to 100 users', fee: '£2,995' },
        { users: '101 to 250 users', fee: '£3,995' },
      ],
      points: [
        'Produced to ILM standards, with ILM-accredited options available',
        '50+ interactive leadership modules across three programmes',
        'Optional bolt-on to any IMPROVE package',
        'Designed for growing organisations, not corporate complexity',
      ],
    };
  },
};
</script>

<style scoped>
.programmes-section {
  padding: 24px 0 8px;
}

.programmes__title {
  text-align: center;
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 28px;
}

.programmes {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 980px;
  margin: 0 auto;
}

.programme-card {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 22px 18px;
}

.programme-card h3 {
  color: var(--brand-navy, #002458);
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 10px;
}

.hero-sub {
  margin: 10px 0 0;
  color: var(--brand-grey, #6b7280);
  font-size: 15px;
}

.programme-modules {
  margin: 12px 0 0;
  padding-left: 18px;
  color: var(--brand-navy, #002458);
  font-size: 13.5px;
  line-height: 1.5;
}

.programme-modules li + li {
  margin-top: 3px;
}

.lp-pricing-section {
  padding: 24px 0 8px;
}

.lp-pricing__lead,
.lp-pricing__note {
  text-align: center;
  color: var(--brand-grey, #6b7280);
  margin: 0 auto 18px;
  max-width: 720px;
}

.lp-pricing {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 820px;
  margin: 0 auto 14px;
}

.lp-pricing__tier {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: #fff;
  border: 2px solid var(--brand-teal, #08a0a8);
  border-radius: 12px;
  padding: 20px 14px;
}

.lp-pricing__users {
  font-size: 14px;
  font-weight: 700;
  color: var(--brand-grey, #6b7280);
  letter-spacing: 0.04em;
}

.lp-pricing__fee {
  font-size: 30px;
  font-weight: 800;
  color: var(--brand-navy, #002458);
}

.lp-pricing__per {
  font-size: 13px;
  color: var(--brand-grey, #6b7280);
}

.lp-pricing__cta {
  text-align: center;
  margin-top: 6px;
}

.lp-pricing__btn {
  display: inline-block;
  padding: 12px 26px;
  border-radius: 6px;
  background: var(--brand-navy, #002458);
  color: #fff !important;
  font-weight: 700;
  text-decoration: none;
}

.lp-pricing__btn:hover {
  background: var(--brand-teal, #08a0a8);
}

@media screen and (max-width: 900px) {
  .lp-pricing {
    grid-template-columns: 1fr;
  }
}

.programme-card p {
  color: var(--brand-grey, #6b7280);
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}

@media screen and (max-width: 900px) {
  .programmes {
    grid-template-columns: 1fr;
  }
}
</style>
